import { BaseCategory, SlideBuilder } from "./BaseCategory";
import { BackgroundStyleType, ForeColorType, FormatType, HeaderPositionType, HorizontalAlignType, ListStyleType, PaletteColorType, TextStyleType, TrayType } from "../constants";
import { v4 as uuid } from "uuid";
import { AiGeneratedSlideType } from "../aiConstants";
import { SlideCategories } from "./CategoryController";
import { formatter } from "../formatter";
import { SlideModelBuilder } from "./SlideModelBuilder";
import _ from "lodash";

export class FactoidCategory extends BaseCategory {
    get name() {
        return "Comparison";
    }

    get type() {
        return SlideCategories.FACTOID;
    }

    get icon() {
        return null;
    }

    get description() {
        return "The content contains 1 to 6 statistics, metrics or facts. Do not use for more than 6 items.";
        // return "The content contains one or more statistics, metrics or facts that are not being directly comparable with each other";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `List each fact or statistic for the slide. You must include at least one stat. If possible, shorten non-numerical facts into concise text. For numerical facts, include units (e.g., %, $, K, M). For each fact or statistic, provide an optional caption and note/description if found in the source content. Optionally, create a conclusion or takeaway for the slide.`;
    }

    getImagePrompt() {
        return false;
    }

    getPromptSchema() {
        return {
            stats: [{
                label: "optional string",
                icon: "search term for an icon representing the fact or statistic",
                text: "the fact or statistic, if numerical include units (e.g., 75%, $1.2M, 350K)",
                // note: "an optional short note, description or additional text for the fact or statistic. only include if found in the source content",
                factType: "number | percentage | currency | text",
                // currencyFormat: "USD | EUR | GBP | JPY (only if factType is currency)",
            }],
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    getSlideModels(model) {
        let models = [
            this.buildFactoid(model),
        ];

        return models;
    }

    buildFactoid(model) {
        // take the first 8 stats only
        let cells = model.stats?.slice(0, 8);

        let builder = new SlideModelBuilder("dashboard", model);
        builder.setBackgroundColor(PaletteColorType.BACKGROUND_LIGHT);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });
        builder.createPrimary({
            fullBleed: false,
            showGutter: true,
            cellColor: ForeColorType.COLORFUL,
            gridLayout: {
                rows: 12,
                cols: 12,
                cells: DashboardLayouts.find(layout => layout.cells.length == cells.length).cells
            },
            items: cells?.map(item => ({
                componentType: "Statistic",
                cellColor: PaletteColorType.BACKGROUND_ACCENT,
                showTitle: _.isEmpty(item.label) ? false : "above",
                chartTitle: {
                    chartTitle: builder.createSingleBlockText(item.label, "_use_styles_"),
                    textAlign: HorizontalAlignType.LEFT
                },
                value: builder.createSingleBlockText(item.text, "_use_styles_"),
                // note: builder.createSingleBlockText(item.note, "_use_styles_"),
                horizontalAlign: HorizontalAlignType.LEFT
            })) || [],
        });
        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }
        return builder.model;
    }
}

const DashboardLayouts = [
    {
        // 1-chart
        cells: [{
            x: 0, y: 0, width: 12, height: 12
        }],
        favorite: true
    },
    {
        // 2-charts-horizontal
        cells: [{
            x: 0, y: 0, width: 6, height: 12
        }, {
            x: 6, y: 0, width: 6, height: 12
        }],
        favorite: true
    },
    {
        // 2-charts-vertical
        cells: [{
            x: 0, y: 0, width: 12, height: 6
        }, {
            x: 0, y: 6, width: 12, height: 6
        }],
        favorite: true
    },
    {
        // 3-charts-left
        cells: [{
            x: 0, y: 0, width: 4, height: 12
        }, {
            x: 4, y: 0, width: 4, height: 12
        }, {
            x: 8, y: 0, width: 4, height: 12
        }],
        favorite: true
    },
    {
        // 3-charts-right
        cells: [{
            x: 0, y: 0, width: 6, height: 6
        }, {
            x: 0, y: 6, width: 6, height: 6
        }, {
            x: 6, y: 0, width: 6, height: 12
        }],
    },
    {
        // 6-charts-top
        cells: [{
            x: 0, y: 0, width: 12, height: 6
        }, {
            x: 0, y: 6, width: 6, height: 6
        }, {
            x: 6, y: 6, width: 6, height: 6
        }],
        favorite: true
    },
    {
        // 3-charts-horizontal
        cells: [{
            x: 0, y: 0, width: 4, height: 12
        }, {
            x: 4, y: 0, width: 4, height: 12
        }, {
            x: 8, y: 0, width: 4, height: 12
        }],
    },
    {
        // 4-charts
        cells: [{
            x: 0, y: 0, width: 6, height: 6
        }, {
            x: 0, y: 6, width: 6, height: 6
        }, {
            x: 6, y: 0, width: 6, height: 6
        }, {
            x: 6, y: 6, width: 6, height: 6
        }],
        favorite: true
    },
    {
        cells: [{
            x: 0, y: 0, width: 4, height: 4
        }, {
            x: 0, y: 4, width: 4, height: 4
        }, {
            x: 0, y: 8, width: 4, height: 4
        }, {
            x: 4, y: 0, width: 8, height: 12
        }]
    },
    {
        cells: [{
            x: 0, y: 0, width: 8, height: 12
        }, {
            x: 8, y: 0, width: 4, height: 4
        }, {
            x: 8, y: 4, width: 4, height: 4
        }, {
            x: 8, y: 8, width: 4, height: 4
        }],
        favorite: true
    },
    {
        cells: [{
            x: 0, y: 0, width: 4, height: 4
        }, {
            x: 4, y: 0, width: 4, height: 4
        }, {
            x: 8, y: 0, width: 4, height: 4
        }, {
            x: 0, y: 4, width: 12, height: 8
        }],
        favorite: true
    },
    {
        cells: [{
            x: 0, y: 0, width: 12, height: 8
        }, {
            x: 0, y: 8, width: 3, height: 4
        }, {
            x: 3, y: 8, width: 3, height: 4
        }, {
            x: 6, y: 8, width: 3, height: 4
        }, {
            x: 9, y: 8, width: 3, height: 4
        }]
    },
    {
        // six cells
        cells: [{
            x: 0, y: 0, width: 4, height: 6
        }, {
            x: 4, y: 0, width: 4, height: 6
        }, {
            x: 8, y: 0, width: 4, height: 6
        }, {
            x: 0, y: 6, width: 4, height: 6
        }, {
            x: 4, y: 6, width: 4, height: 6
        }, {
            x: 8, y: 6, width: 4, height: 6
        }]
    },
    {
        // seven cells
        cells: [{
            x: 0, y: 0, width: 6, height: 12
        }, {
            x: 6, y: 0, width: 2, height: 6
        }, {
            x: 8, y: 0, width: 2, height: 6
        }, {
            x: 10, y: 0, width: 2, height: 6
        }, {
            x: 6, y: 6, width: 2, height: 6
        }, {
            x: 8, y: 6, width: 2, height: 6
        }, {
            x: 10, y: 6, width: 2, height: 6
        }]
    },
    {
        // eight cells
        cells: [{
            x: 0, y: 0, width: 3, height: 6
        }, {
            x: 3, y: 0, width: 3, height: 6
        }, {
            x: 6, y: 0, width: 3, height: 6
        }, {
            x: 9, y: 0, width: 3, height: 6
        }, {
            x: 0, y: 6, width: 3, height: 6
        }, {
            x: 3, y: 6, width: 3, height: 6
        }, {
            x: 6, y: 6, width: 3, height: 6
        }, {
            x: 9, y: 6, width: 3, height: 6
        }]
    }
];

